// <s-promo-popup> opens its newsletter dialog after a delay, remembers dismissal, and reopens on a form result.
const STORAGE_KEY = 'promo-popup-hidden-until';

class SPromoPopup extends HTMLElement {
  #ac;
  #timeoutId = 0;

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;

    this.#ac = new AbortController();
    const { signal } = this.#ac;

    this.dialog.addEventListener('close', () => this.#suppress(), { signal });
    // CommandEvent does not bubble, so listen on the dialog that copy buttons target.
    this.dialog.addEventListener(
      'command',
      (event) => {
        if (event.command === '--copy') this.#copy(event.source);
      },
      { signal },
    );

    // A posted signup (success or error) reopens immediately so the buyer sees the outcome.
    if (this.querySelector('[data-promo-result]')) {
      requestAnimationFrame(() => this.#open());
      return;
    }

    if (this.hasAttribute('data-design-mode') || this.#isSuppressed()) return;

    const delayMs = Math.max(0, Number(this.dataset.delay) || 0) * 1000;
    this.#timeoutId = window.setTimeout(() => this.#open(), delayMs);
  }

  disconnectedCallback() {
    window.clearTimeout(this.#timeoutId);
    this.#ac?.abort();
  }

  #open() {
    // Never stack on top of another open modal such as the cart drawer.
    if (this.dialog.open || document.querySelector('dialog:modal')) return;
    this.dialog.showModal();
  }

  #isSuppressed() {
    try {
      return Number(localStorage.getItem(STORAGE_KEY)) > Date.now();
    } catch {
      return false;
    }
  }

  #suppress() {
    const days = Math.max(1, Number(this.dataset.days) || 30);
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now() + days * 86400000));
    } catch {
      // Storage unavailable: the popup simply may show again next visit.
    }
  }

  async #copy(source) {
    const codeElement = this.querySelector('[data-promo-code]');
    const code = codeElement?.textContent.trim();
    if (!code || !(source instanceof HTMLElement)) return;

    try {
      await navigator.clipboard.writeText(code);
    } catch {
      return;
    }

    const status = this.querySelector('[data-promo-copy-status]');
    if (status) {
      status.textContent = '';
      requestAnimationFrame(() => {
        status.textContent = source.dataset.copiedMessage || '';
      });
    }
  }
}

if (!customElements.get('s-promo-popup')) customElements.define('s-promo-popup', SPromoPopup);
